import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import { offerAttestation } from './offerAttestation';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeftIcon, ClockIcon, CalendarDaysIcon, ChartBarIcon,
  PlusIcon, MinusCircleIcon, CheckCircleIcon, XMarkIcon,
  ExclamationTriangleIcon, IdentificationIcon, PencilSquareIcon,
  DocumentTextIcon, BanknotesIcon, NoSymbolIcon, PauseCircleIcon,
  PlayCircleIcon, DocumentArrowUpIcon, ArrowTopRightOnSquareIcon,
  FolderOpenIcon, ListBulletIcon, ChatBubbleLeftRightIcon, CheckIcon,
  EnvelopeIcon, PhoneIcon, SparklesIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import EmployeeFile from '../../components/EmployeeFile';
import WalletBalanceCard from '../../components/WalletBalanceCard';
import DiscProfile from '../../components/shared/DiscProfile';
import StatusBadge from '../../components/shared/StatusBadge';
import { TaskDetailModal } from './TasksPage';
import { etLocalInputToInstant, instantToEtLocalInput, formatEtDateTime } from '../../lib/etTime';
// hire_date is a DATE column — never a JS Date. See lib/dateOnly.js.
import { formatDateOnlyNumeric } from '../../lib/dateOnly';
import { DATE_FLOOR, DOB_FLOOR, completionMax, expiryMax } from '../../lib/dateBounds';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EVENT_TYPES = [
  { val: 'late_arrival', label: 'Late arrival' },
  { val: 'late_return_from_break', label: 'Late returning from break' },
  { val: 'call_off_advance', label: 'Called off >2hr in advance' },
  { val: 'call_off_short_notice', label: 'Called off <2hr notice' },
  { val: 'call_off_holiday', label: 'Called off near holiday' },
  { val: 'missing_partial_shift', label: 'Missed >2hr of shift' },
  { val: 'no_show', label: 'No call no show' },
  { val: 'missed_meeting', label: 'Missed mandatory meeting' },
  { val: 'excused_doctor_note', label: 'Excused (doctor note)' },
  { val: 'clock_in', label: 'Clock-in (no points)' },
  { val: 'clock_out', label: 'Clock-out (no points)' },
];

// Clock/event times are true UTC instants — always render them in Eastern,
// never the viewer's browser timezone.
const formatDateTime = (d) => d ? formatEtDateTime(d) : '—';
// formatDate is for TIMESTAMP columns only (uploaded_at, completed_at).
const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
// formatDay is for DATE columns — hire, expiry, completion, probation, point
// expiry, due dates. Through `new Date()` every one of them rendered a day
// early in an Eastern browser (census #194). Never a JS Date; see lib/dateOnly.js.
const formatDay = (d) => (d && formatDateOnlyNumeric(d)) || '—';

export default function StaffAttendanceDetailPage() {
  const { staffId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/attendance/staff/${staffId}`);
      setData(d);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  if (!data) return null;

  const { staff, balance, threshold, events, ledger } = data;

  return (
    <div className="animate-slide-up">
      <Link to="/attendance" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-orange-500 mb-3">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Attendance
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {staff.photo_url ? (
              <img src={staff.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-semibold">
                {(staff.first_name?.[0] || '') + (staff.last_name?.[0] || '')}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{staff.first_name} {staff.last_name}</h1>
              <p className="text-sm text-gray-500">{staff.role} · {staff.employment_type === 'part_time' ? 'Part-time' : 'Full-time'}</p>
              <p className="text-xs text-gray-400 mt-0.5">Hired {formatDay(staff.hire_date)}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-gray-900">{Number(balance).toFixed(1)}</p>
            <p className="text-xs text-gray-500">points</p>
          </div>
        </div>

        {threshold && (
          <div className={`mt-4 p-3 rounded-xl border ${
            threshold.color === 'red'
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">{threshold.action_label} (at {Number(threshold.points_at).toFixed(1)} pts)</p>
                {threshold.description && <p className="text-xs mt-1 opacity-90">{threshold.description}</p>}
              </div>
            </div>
          </div>
        )}

        {staff.attendance_probation_until && new Date(staff.attendance_probation_until) >= new Date() && (
          <div className="mt-3 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-sm">
            On attendance probation until <strong>{formatDay(staff.attendance_probation_until)}</strong>
          </div>
        )}

        <div className="flex gap-2 mt-4 flex-wrap">
          <button onClick={() => setShowLog(true)} className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 inline-flex items-center gap-1">
            <PlusIcon className="w-4 h-4" /> Log Event
          </button>
          <button onClick={() => setShowAdjust(true)} className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-1">
            <MinusCircleIcon className="w-4 h-4" /> Adjust Points
          </button>
          <button onClick={() => setShowEditProfile(true)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-1">
            <PencilSquareIcon className="w-4 h-4" /> Edit Profile
          </button>
          <SetPasswordButton staffId={staff.id} staffName={`${staff.first_name} ${staff.last_name}`} />
          <MarkOfferSignedButton staff={staff} onDone={load} />
          <VoidStaffButton staffId={staff.id} staffName={`${staff.first_name} ${staff.last_name}`} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'overview', label: 'Profile', icon: IdentificationIcon },
          { id: 'file', label: 'Employment File', icon: FolderOpenIcon },
          { id: 'tasks', label: 'Tasks', icon: ListBulletIcon },
          { id: 'events', label: 'Time & Events', icon: ClockIcon },
          { id: 'ledger', label: 'Point Ledger', icon: ChartBarIcon },
          { id: 'schedule', label: 'Schedule', icon: CalendarDaysIcon },
          { id: 'template', label: 'Weekly Template', icon: SparklesIcon },
          { id: 'profile', label: 'Scheduling Profile', icon: IdentificationIcon },
          { id: 'goals', label: 'Goal Scoring', icon: ChartBarIcon },
          { id: 'performance', label: 'Performance', icon: ChartBarIcon },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <ProfileOverviewTab staff={staff} reload={load} />}
      {tab === 'file' && <StaffFileTab staffId={staffId} />}
      {tab === 'tasks' && <StaffTasksTab staffId={staffId} staffName={`${staff.first_name} ${staff.last_name}`} />}
      {tab === 'events' && <EventsTab events={events} reload={load} />}
      {tab === 'ledger' && <LedgerTab ledger={ledger} onChange={load} />}
      {tab === 'schedule' && <ScheduleTab staffId={staffId} />}
      {tab === 'template' && <WeeklyTemplateTab staffId={staffId} />}
      {tab === 'profile' && (
        <div className="space-y-6">
          {/* Per director (2026-06-04): Contact & Availability merged
              into Scheduling Profile so there's one canonical place
              for "everything about this person's schedule." */}
          <ContactAvailabilityTab staffId={staffId} />
          <SchedulingProfileTab staffId={staffId} />
        </div>
      )}
      {tab === 'goals' && <GoalScoringTab staffId={staffId} />}
      {tab === 'performance' && <PerformanceTab staffId={staffId} role={staff.role} />}

      {showLog && <LogEventModal staffId={staffId} onClose={() => setShowLog(false)} onDone={() => { setShowLog(false); load(); }} />}
      {showAdjust && <AdjustModal staffId={staffId} onClose={() => setShowAdjust(false)} onDone={() => { setShowAdjust(false); load(); }} />}
      {showEditProfile && (
        <EditProfileModal
          staff={staff}
          onClose={() => setShowEditProfile(false)}
          onSaved={() => { setShowEditProfile(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Tasks tab — every task assigned to this staff member ───
// Access is enforced server-side by canViewStaff (director: anyone;
// admin: teacher/float/cook only, not other admins; staff: self only).
function StaffTasksTab({ staffId, staffName }) {
  const { isManagement } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get(`/staff/${staffId}/tasks`)];
      // Reassign dropdown (inside the detail modal) needs the staff list,
      // which only management can fetch / act on.
      if (isManagement) reqs.push(api.get('/staff'));
      const [taskRes, staffRes] = await Promise.all(reqs);
      setTasks(taskRes.data);
      if (staffRes) setStaffList(staffRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load tasks');
    } finally { setLoading(false); }
  }, [staffId, isManagement]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;

  const openTasks = tasks.filter(t => t.status !== 'complete');
  const doneTasks = tasks.filter(t => t.status === 'complete');
  const priorityBorder = { high: 'border-l-red-500', medium: 'border-l-amber-500', low: 'border-l-green-500' };

  const renderCard = (task) => {
    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'complete';
    return (
      <div key={task.id} className={`bg-white rounded-2xl border border-l-4 ${priorityBorder[task.priority] || 'border-l-gray-300'} p-4 hover:shadow-md transition-shadow ${isOverdue ? 'bg-red-50/30' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <button onClick={() => setDetailId(task.id)}
                className={`font-medium text-left hover:text-orange-600 transition-colors ${task.status === 'complete' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                {task.title}
              </button>
              {task.requires_dual_signoff && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">Dual</span>}
              {task.auto_source === 'ai_interview_followup' && (
                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">✨ From interview</span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
              <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{task.area?.replace(/_/g, ' ')}</span>
              {task.due_date && <span className={isOverdue ? 'text-red-500 font-medium' : ''}>Due: {formatDay(task.due_date)}</span>}
              {Number(task.note_count) > 0 && (
                <span className="inline-flex items-center gap-1 text-gray-400"><ChatBubbleLeftRightIcon className="w-3.5 h-3.5" /> {task.note_count}</span>
              )}
            </div>
            {task.status === 'complete' && task.completed_by_name && (
              <p className="text-xs text-emerald-600 mt-1.5 inline-flex items-center gap-1">
                <CheckIcon className="w-3.5 h-3.5" /> Completed by {task.completed_by_name}{task.completed_at ? ` · ${formatDate(task.completed_at)}` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge status={task.priority} />
            <StatusBadge status={task.status} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {tasks.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-2xl border border-gray-200">
          <ListBulletIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No tasks assigned to {staffName}.</p>
        </div>
      ) : (
        <>
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Open ({openTasks.length})</h3>
            {openTasks.length === 0
              ? <p className="text-sm text-gray-400">Nothing open.</p>
              : <div className="space-y-3">{openTasks.map(renderCard)}</div>}
          </div>
          {doneTasks.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Completed ({doneTasks.length})</h3>
              <div className="space-y-3">{doneTasks.map(renderCard)}</div>
            </div>
          )}
        </>
      )}

      {detailId && (
        <TaskDetailModal
          taskId={detailId}
          staffList={staffList}
          isManagement={isManagement}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function EventsTab({ events, reload }) {
  const [editing, setEditing] = useState(null);
  const excuse = async (eventId) => {
    const reason = window.prompt('Reason to excuse this event (e.g. "Doctor\'s note received"):');
    if (!reason) return;
    try {
      await api.post(`/attendance/events/${eventId}/excuse`, { excuse_reason: reason });
      toast.success('Event excused, points reversed');
      reload();
    } catch { toast.error('Failed to excuse'); }
  };

  const removeEvent = async (eventId) => {
    if (!window.confirm('Delete this event? If it charged points or a wallet penalty, those get refunded too.')) return;
    try {
      await api.delete(`/attendance/events/${eventId}`);
      toast.success('Event deleted, any point/wallet charge reversed');
      reload();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  if (!events?.length) {
    return <div className="text-center py-8 text-sm text-gray-400 bg-white rounded-2xl border border-gray-200">No events recorded</div>;
  }

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase">
              <th className="px-6 py-3">When</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Late</th>
              <th className="px-6 py-3">Source</th>
              <th className="px-6 py-3">Notes</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {events.map(e => (
              <tr key={e.id} className={e.excused ? 'opacity-50' : ''}>
                <td className="px-6 py-3 text-sm text-gray-600">{formatDateTime(e.occurred_at)}</td>
                <td className="px-6 py-3 text-sm text-gray-900 font-medium">
                  {e.event_type.replace(/_/g, ' ')}
                  {e.excused && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Excused</span>}
                </td>
                <td className="px-6 py-3 text-sm text-gray-600">
                  {e.minutes_late != null ? `${e.minutes_late} min` : '—'}
                </td>
                <td className="px-6 py-3 text-xs text-gray-500">{e.source.replace(/_/g, ' ')}</td>
                <td className="px-6 py-3 text-xs text-gray-600 max-w-xs truncate">{e.notes || '—'}</td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(e)}
                    className="text-xs text-orange-600 hover:underline mr-3">
                    Edit time
                  </button>
                  {!e.excused && e.event_type !== 'clock_in' && e.event_type !== 'clock_out' && (
                    <button onClick={() => excuse(e.id)}
                      className="text-xs text-blue-600 hover:underline mr-3">
                      Excuse
                    </button>
                  )}
                  <button onClick={() => removeEvent(e.id)}
                    className="text-xs text-red-600 hover:underline">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <EditEventTimeModal event={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
    </>
  );
}

// Correct the time on a clock in/out (or any attendance event) when
// staff forgot to punch out, punched twice, etc. The reason field
// gets appended to notes so the audit trail explains why the row
// was changed. Late-arrival rows recompute minutes_late server-side.
function EditEventTimeModal({ event, onClose, onSaved }) {
  // occurred_at is a true UTC instant. Seed the picker from its Eastern
  // wall-clock (browser-TZ independent) and send back a true UTC instant,
  // matching how the clock-in writer stores punches.
  const [occurredAt, setOccurredAt] = useState(instantToEtLocalInput(event.occurred_at));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!occurredAt) return toast.error('Pick the correct time');
    setSaving(true);
    try {
      const notes = reason.trim()
        ? `${event.notes ? event.notes + '\n' : ''}[Time edited: ${reason.trim()}]`
        : undefined;
      await api.patch(`/attendance/events/${event.id}`, {
        // The picker holds Eastern wall-clock; convert to a true UTC instant
        // so it agrees with clock-in punches (stored as UTC) and displays
        // correctly everywhere.
        occurred_at: etLocalInputToInstant(occurredAt),
        ...(notes ? { notes } : {}),
      });
      toast.success('Time corrected');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3">
          <h3 className="text-base font-semibold text-gray-900">Correct time — {event.event_type.replace(/_/g, ' ')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Recorded at {formatDateTime(event.occurred_at)}. Change to the actual time.</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Correct time</label>
            <input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Reason (optional)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Forgot to clock out — set to shift end 6:00 PM"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LedgerTab({ ledger, onChange }) {
  const [activeId, setActiveId] = useState(null);
  if (!ledger?.length) {
    return <div className="text-center py-8 text-sm text-gray-400 bg-white rounded-2xl border border-gray-200">No point activity yet</div>;
  }
  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase">
              <th className="px-6 py-3">When</th>
              <th className="px-6 py-3">Change</th>
              <th className="px-6 py-3">Reason</th>
              <th className="px-6 py-3">Notes</th>
              <th className="px-6 py-3">Expires</th>
              <th className="px-6 py-3">By</th>
              <th className="px-6 py-3 text-right">Edit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {ledger.map(l => {
              const change = Number(l.points_change);
              return (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3">
                    <button onClick={() => setActiveId(l.id)}
                      className="text-sm text-orange-600 hover:text-orange-700 hover:underline font-medium text-left">
                      {formatDateTime(l.created_at)}
                    </button>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      change > 0 ? 'bg-red-50 text-red-700' : change < 0 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
                    }`}>
                      {change > 0 ? '+' : ''}{change.toFixed(1)}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">{l.rule_label || l.reason}</td>
                  <td className="px-6 py-3 text-xs text-gray-500 max-w-xs truncate">{l.notes || '—'}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">{formatDay(l.expires_at)}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {l.created_by_first ? `${l.created_by_first} ${l.created_by_last || ''}` : 'System'}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setActiveId(l.id)}
                      title="Edit this entry"
                      className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-orange-50">
                      <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {activeId && (
        <LedgerEntryModal
          ledgerId={activeId}
          onClose={() => setActiveId(null)}
          onSaved={() => { setActiveId(null); onChange?.(); }}
        />
      )}
    </>
  );
}

function LedgerEntryModal({ ledgerId, onClose, onSaved }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/attendance/ledger/${ledgerId}`)
      .then(({ data }) => {
        setEntry(data);
        setDraft({
          notes: data.notes || '',
          points_change: Number(data.points_change),
          expires_at: data.expires_at?.slice(0, 10) || '',
        });
      })
      .catch(() => toast.error('Failed to load entry'))
      .finally(() => setLoading(false));
  }, [ledgerId]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        notes: draft.notes || null,
        points_change: Number(draft.points_change),
        expires_at: draft.expires_at || null,
      };
      await api.patch(`/attendance/ledger/${ledgerId}`, patch);
      toast.success('Updated');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Point Entry" onClose={onClose}>
      {loading || !entry ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>
      ) : (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Detail label="Created" value={formatDateTime(entry.created_at)} />
            <Detail label="By" value={entry.created_by_first ? `${entry.created_by_first} ${entry.created_by_last || ''}` : 'System'} />
            <Detail label="Reason" value={entry.rule_label || entry.reason} />
            <Detail label="Expires" value={formatDay(entry.expires_at) || 'Never'} />
          </div>

          {/* Linked event details (if this point came from an attendance event) */}
          {entry.event_type && (
            <div className="bg-gray-50 rounded-xl p-3 text-xs space-y-1">
              <p className="font-semibold text-gray-700 mb-1">Linked attendance event</p>
              <p><strong>Type:</strong> {entry.event_type.replace(/_/g, ' ')}</p>
              <p><strong>Occurred:</strong> {formatDateTime(entry.occurred_at)}</p>
              {entry.scheduled_start && <p><strong>Scheduled start:</strong> {formatDateTime(entry.scheduled_start)}</p>}
              {entry.minutes_late != null && <p><strong>Minutes late:</strong> {entry.minutes_late}</p>}
              {entry.event_source && <p><strong>Source:</strong> {entry.event_source.replace(/_/g, ' ')}</p>}
              {entry.event_notes && <p><strong>Event notes:</strong> {entry.event_notes}</p>}
              {entry.excused && <p className="text-green-700"><strong>Excused:</strong> {entry.excuse_reason || '(no reason given)'}</p>}
              {entry.doctor_note_url && (
                <p><a href={entry.doctor_note_url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">View doctor's note</a></p>
              )}
            </div>
          )}

          {/* Edit form */}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Admin edit</p>
              {!editing && (
                <button onClick={() => setEditing(true)}
                  className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-200">
                  Edit
                </button>
              )}
            </div>

            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Points change</label>
                    <input type="number" step="0.5" value={draft.points_change}
                      onChange={(e) => setDraft({ ...draft, points_change: e.target.value })}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Expires</label>
                    <input type="date" min={DATE_FLOOR} value={draft.expires_at}
                      onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                  <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    rows={3}
                    placeholder="Add or edit notes — context, what was discussed, why you're adjusting…"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={save} disabled={saving}
                    className="flex-1 bg-orange-500 text-white py-2 rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Notes</p>
                  <p className="text-gray-800 whitespace-pre-wrap">{entry.notes || <span className="text-gray-400">No notes — click Edit to add</span>}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{value || '—'}</p>
    </div>
  );
}

// Per director (2026-06-04): this tab used to host the "Recurring
// Weekly Schedule" template (a second copy of availability_pattern,
// which is already managed on the Scheduling Profile tab). Now it
// shows the actual team schedule for this staff — read-only.
// Director still edits via the Team Schedule grid.
function ScheduleTab({ staffId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const dow = d.getDay();
    const back = dow === 0 ? 6 : dow - 1;
    d.setDate(d.getDate() - back);
    return d;
  });

  const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const formatISO = (d) => d.toISOString().slice(0, 10);
  const formatTime = (t) => {
    if (!t) return '—';
    const [h, m] = String(t).split(':').map(Number);
    if (Number.isNaN(h)) return t;
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/team-schedule/week?start=${formatISO(weekStart)}`);
      setData(d);
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;
  if (!data) return null;

  const myRow = data.rows.find(r => Number(r.id) === Number(staffId));
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${addDays(weekStart, 4).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Team schedule — week of {weekLabel}</h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="text-xs text-gray-500 hover:text-orange-600 px-2 py-1 rounded hover:bg-gray-100">
            ‹ Prev
          </button>
          <button onClick={() => {
            const d = new Date(); d.setHours(0, 0, 0, 0);
            const dow = d.getDay(); const back = dow === 0 ? 6 : dow - 1;
            d.setDate(d.getDate() - back); setWeekStart(d);
          }} className="text-xs text-gray-500 hover:text-orange-600 px-2 py-1 rounded hover:bg-gray-100">
            This week
          </button>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="text-xs text-gray-500 hover:text-orange-600 px-2 py-1 rounded hover:bg-gray-100">
            Next ›
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Read-only view. To edit, use the <Link to="/team-schedule" className="text-orange-600 hover:underline">Team Schedule</Link> page.
      </p>

      {!myRow ? (
        <div className="text-center py-6 text-sm text-gray-400">
          No schedule data for this staff member this week.
        </div>
      ) : (
        <div className="space-y-2">
          {weekDays.map((d, i) => {
            const iso = formatISO(d);
            const cell = myRow.cells?.find(c => c.date === iso);
            const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
            return (
              <div key={iso} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50/50">
                <div className="text-xs font-semibold text-gray-700 w-20 flex-shrink-0">{dayLabel}</div>
                <div className="flex-1 text-sm text-gray-800">
                  {!cell || cell.status === 'off' || cell.source === 'none' || !cell.start_time ? (
                    <span className="text-gray-400">Off</span>
                  ) : cell.status === 'call_off' ? (
                    <span className="text-red-600 font-semibold">Call off</span>
                  ) : cell.status === 'time_off_approved' ? (
                    <span className="text-gray-600">Off request (approved)</span>
                  ) : cell.status === 'time_off_pending' ? (
                    <span className="text-amber-700 font-semibold">Off request (pending)</span>
                  ) : cell.status === 'center_closed' ? (
                    <span className="text-violet-700">CLOSED</span>
                  ) : Array.isArray(cell.segments) && cell.segments.length > 0 ? (
                    <div className="space-y-0.5">
                      {cell.segments.map((seg, si) => (
                        <div key={si} className="text-xs">
                          {formatTime(seg.start)}–{formatTime(seg.end)}
                          {seg.classroom && <span className="text-gray-500"> · {seg.classroom}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span>
                      {formatTime(cell.start_time)}–{formatTime(cell.end_time)}
                      {cell.classroom && <span className="text-gray-500"> · {cell.classroom}</span>}
                      {cell.lunch_break && <span className="text-gray-400 text-xs"> · lunch {cell.lunch_break}</span>}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LogEventModal({ staffId, onClose, onDone }) {
  // Prefill "when" with Eastern now (the datetime-local picker is wall-clock).
  const { register, handleSubmit, watch } = useForm({
    defaultValues: { event_type: 'late_arrival', occurred_at: instantToEtLocalInput(new Date().toISOString()), excused: false },
  });
  const submit = async (form) => {
    try {
      await api.post(`/attendance/staff/${staffId}/events`, {
        ...form,
        // Picker holds Eastern wall-clock → store a true UTC instant.
        occurred_at: etLocalInputToInstant(form.occurred_at),
        excused: form.excused === 'yes' || form.excused === true,
        minutes_late: form.minutes_late ? Number(form.minutes_late) : null,
      });
      toast.success('Event logged');
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to log'); }
  };
  const isExcused = watch('excused') === 'yes';
  return (
    <Modal title="Log Attendance Event" onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
          <select {...register('event_type', { required: true })}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm bg-white focus:border-orange-500 focus:outline-none">
            {EVENT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">When</label>
          <input type="datetime-local" {...register('occurred_at', { required: true })}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minutes Late</label>
            <input type="number" {...register('minutes_late')}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excused?</label>
            <select {...register('excused')} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm bg-white">
              <option value="no">No (charge points)</option>
              <option value="yes">Yes (no points)</option>
            </select>
          </div>
        </div>
        {isExcused && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excuse Reason</label>
            <input {...register('excuse_reason')} placeholder="e.g. Doctor's note 5/8 — strep"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea {...register('notes')} rows={2}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none resize-none" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">Log Event</button>
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function AdjustModal({ staffId, onClose, onDone }) {
  const { register, handleSubmit } = useForm();
  const submit = async (form) => {
    try {
      await api.post(`/attendance/staff/${staffId}/adjust`, {
        points_change: Number(form.points_change),
        notes: form.notes,
      });
      toast.success('Adjustment recorded');
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to adjust'); }
  };
  return (
    <Modal title="Manual Point Adjustment" onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <p className="text-xs text-gray-500">Negative number forgives points. Positive adds. All adjustments are logged in the ledger.</p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Points Change</label>
          <input {...register('points_change', { required: true })} type="number" step="0.5" placeholder="-1.0"
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason (required)</label>
          <textarea {...register('notes', { required: true })} rows={3}
            placeholder='e.g. "Forgive 1 pt — proven family emergency"'
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none resize-none" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">Apply</button>
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// Weekly Template — the recurring shift pattern that gets copied into
// the Team Schedule when the director clicks "Use templates". Mon–Fri
// only (BBC doesn't operate weekends). Each day: enable + start + end
// + classroom. Save wipes and replaces every recurring row for this
// staff, so the form always shows the current source of truth.
const TEMPLATE_DAYS = [
  { dow: 1, key: 'monday',    label: 'Monday' },
  { dow: 2, key: 'tuesday',   label: 'Tuesday' },
  { dow: 3, key: 'wednesday', label: 'Wednesday' },
  { dow: 4, key: 'thursday',  label: 'Thursday' },
  { dow: 5, key: 'friday',    label: 'Friday' },
];

function WeeklyTemplateTab({ staffId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classrooms, setClassrooms] = useState([]);
  const [rows, setRows] = useState(() => TEMPLATE_DAYS.map(d => ({
    dow: d.dow, key: d.key, label: d.label,
    enabled: false, start: '', end: '', classroom: '',
  })));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/attendance/staff/${staffId}/schedule`),
      api.get('/classrooms').catch(() => ({ data: [] })),
    ])
      .then(([sched, rooms]) => {
        if (cancelled) return;
        setClassrooms(rooms.data || []);
        // Seed rows from existing recurring shifts. Only one shift per
        // day for now — the team schedule UI supports multi-segment
        // shifts, but the template is single-segment for simplicity.
        const byDow = new Map();
        for (const s of (sched.data || [])) {
          if (!byDow.has(s.day_of_week)) byDow.set(s.day_of_week, s);
        }
        setRows(TEMPLATE_DAYS.map(d => {
          const s = byDow.get(d.dow);
          return {
            dow: d.dow, key: d.key, label: d.label,
            enabled: !!s,
            start: s?.start_time ? String(s.start_time).slice(0, 5) : '',
            end:   s?.end_time   ? String(s.end_time).slice(0, 5)   : '',
            classroom: s?.classroom || '',
          };
        }));
      })
      .catch(() => toast.error('Failed to load template'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [staffId]);

  const setRow = (dow, patch) => setRows(rs => rs.map(r => r.dow === dow ? { ...r, ...patch } : r));

  const save = async () => {
    // Basic validation before writing.
    for (const r of rows) {
      if (!r.enabled) continue;
      if (!r.start || !r.end) return toast.error(`${r.label}: set both a start and end time, or turn the row off.`);
      if (r.start >= r.end) return toast.error(`${r.label}: end must be after start.`);
    }
    setSaving(true);
    try {
      const shifts = rows
        .filter(r => r.enabled)
        .map(r => ({
          day_of_week: r.dow,
          start_time: `${r.start}:00`,
          end_time: `${r.end}:00`,
          classroom: r.classroom || null,
        }));
      await api.post(`/attendance/staff/${staffId}/schedule`, { shifts });
      toast.success(shifts.length === 0
        ? 'Template cleared. This staff will no longer auto-fill from templates.'
        : `Template saved — ${shifts.length} weekday shift${shifts.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const copyFrom = (sourceDow) => {
    const src = rows.find(r => r.dow === sourceDow);
    if (!src?.enabled) return;
    setRows(rs => rs.map(r => r.dow === sourceDow ? r : ({
      ...r,
      enabled: true,
      start: src.start,
      end: src.end,
      classroom: src.classroom,
    })));
    toast.success(`Copied ${src.label} to every other day`);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Weekly Template</h3>
        <p className="text-xs text-gray-500 mt-1">
          This staff's usual working hours by day. When you click <strong>Use templates</strong> on the Team Schedule,
          any empty cell in the week gets filled from here.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
              <th className="py-2 pr-3">Day</th>
              <th className="py-2 pr-3">Working?</th>
              <th className="py-2 pr-3">Start</th>
              <th className="py-2 pr-3">End</th>
              <th className="py-2 pr-3">Classroom</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.dow} className="border-b border-gray-50">
                <td className="py-2 pr-3 font-medium text-gray-900">{r.label}</td>
                <td className="py-2 pr-3">
                  <input type="checkbox" checked={r.enabled}
                    onChange={(e) => setRow(r.dow, { enabled: e.target.checked })} />
                </td>
                <td className="py-2 pr-3">
                  <input type="time" value={r.start} disabled={!r.enabled}
                    onChange={(e) => setRow(r.dow, { start: e.target.value })}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400" />
                </td>
                <td className="py-2 pr-3">
                  <input type="time" value={r.end} disabled={!r.enabled}
                    onChange={(e) => setRow(r.dow, { end: e.target.value })}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400" />
                </td>
                <td className="py-2 pr-3">
                  <select value={r.classroom} disabled={!r.enabled}
                    onChange={(e) => setRow(r.dow, { classroom: e.target.value })}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400">
                    <option value="">— (no default)</option>
                    {classrooms.map(c => (
                      <option key={c.id || c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 text-right">
                  {r.enabled && (
                    <button onClick={() => copyFrom(r.dow)}
                      title="Copy this day's shift to every other weekday"
                      className="text-xs text-orange-600 hover:underline">
                      Copy to all
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          Saving here wipes and replaces the recurring rows. Per-day overrides you made on the team schedule stay untouched.
        </p>
        <button onClick={save} disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Template'}
        </button>
      </div>
    </div>
  );
}

function SchedulingProfileTab({ staffId }) {
  const [profile, setProfile] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState('');
  const [skills, setSkills] = useState(new Set());
  const [notes, setNotes] = useState('');
  const [avail, setAvail] = useState({});
  // Primary classroom = the room this staff "lives in" by default.
  // Drives auto-fill when adding shifts on the team schedule.
  const [primaryClassroom, setPrimaryClassroom] = useState('');

  const DAYS_KEYS = [
    { key: 'sunday', label: 'Sun' },
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
  ];

  useEffect(() => {
    Promise.all([
      api.get(`/staff/${staffId}/scheduling-profile`),
      api.get('/classrooms'),
    ]).then(([p, c]) => {
      setProfile(p.data);
      setClassrooms(c.data);
      setTarget(p.data.target_weekly_hours ?? '');
      setNotes(p.data.scheduling_notes || '');
      setPrimaryClassroom(p.data.primary_classroom || '');
      const initialSkills = new Set(
        (p.data.classroom_skills || '').split(',').map(s => s.trim()).filter(Boolean)
      );
      setSkills(initialSkills);

      // availability_pattern shape: { monday: {start,end} | "unavailable", ... }
      const a = p.data.availability_pattern || {};
      const normalized = {};
      for (const d of DAYS_KEYS) {
        const v = a[d.key];
        // BBC is closed weekends — default Sat/Sun to unavailable when
        // the day isn't explicitly set, so existing-staff imports don't
        // accidentally show them available to the scheduler.
        const isWeekend = d.key === 'saturday' || d.key === 'sunday';
        if (v === 'unavailable') {
          normalized[d.key] = { available: false, start: '07:00', end: '17:00' };
        } else if (v == null) {
          normalized[d.key] = { available: !isWeekend, start: '07:00', end: '17:00' };
        } else if (typeof v === 'object') {
          normalized[d.key] = { available: true, start: v.start || '07:00', end: v.end || '17:00' };
        } else {
          normalized[d.key] = { available: true, start: '07:00', end: '17:00' };
        }
      }
      setAvail(normalized);
    }).catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, [staffId]);

  const toggleSkill = (name) => {
    const next = new Set(skills);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSkills(next);
  };

  const setDay = (key, patch) => {
    setAvail({ ...avail, [key]: { ...avail[key], ...patch } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const availability_pattern = {};
      for (const d of DAYS_KEYS) {
        const v = avail[d.key];
        if (!v?.available) availability_pattern[d.key] = 'unavailable';
        else availability_pattern[d.key] = { start: v.start, end: v.end };
      }
      await api.patch(`/staff/${staffId}/scheduling-profile`, {
        target_weekly_hours: target === '' ? null : Number(target),
        availability_pattern,
        classroom_skills: Array.from(skills).join(','),
        scheduling_notes: notes || null,
        primary_classroom: primaryClassroom || null,
      });
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
      <p className="text-sm text-gray-600">
        Inputs the AI scheduler uses to generate this staff member's shifts. Set their target hours, when they're available, and which classrooms they can cover.
      </p>

      {/* Target hours */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Target Weekly Hours</label>
        <div className="flex items-center gap-2">
          <input type="number" value={target} onChange={(e) => setTarget(e.target.value)}
            placeholder="40 (full-time), 20 (part-time)…"
            className="w-32 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
          <span className="text-sm text-gray-500">hours / week</span>
        </div>
      </div>

      {/* Availability */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Availability Pattern</label>
        <p className="text-xs text-gray-500 mb-3">When this staff member can normally work each day.</p>
        <div className="space-y-2">
          {DAYS_KEYS.map(d => {
            const v = avail[d.key] || { available: false, start: '07:00', end: '17:00' };
            return (
              <div key={d.key} className="flex items-center gap-3">
                <label className="flex items-center gap-2 w-28">
                  <input type="checkbox" checked={v.available}
                    onChange={(e) => setDay(d.key, { available: e.target.checked })}
                    className="accent-orange-500" />
                  <span className="text-sm font-medium">{d.label}</span>
                </label>
                {v.available ? (
                  <>
                    <input type="time" value={v.start} onChange={(e) => setDay(d.key, { start: e.target.value })}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-sm" />
                    <span className="text-gray-400 text-sm">to</span>
                    <input type="time" value={v.end} onChange={(e) => setDay(d.key, { end: e.target.value })}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-sm" />
                  </>
                ) : (
                  <span className="text-xs text-gray-400">Unavailable</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Primary (home) classroom */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Primary Classroom</label>
        <p className="text-xs text-gray-500 mb-2">
          The room this staff member normally works in. Auto-fills the classroom dropdown when you add a shift on the team schedule.
        </p>
        <select value={primaryClassroom} onChange={(e) => setPrimaryClassroom(e.target.value)}
          className="w-full max-w-sm rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
          <option value="">— No primary room —</option>
          {[...classrooms].sort((a, b) => a.display_order - b.display_order).map(c => (
            <option key={c.id} value={c.name}>
              {c.name}{c.max_kids_per_staff < 50 ? ` (1:${c.max_kids_per_staff})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Classroom skills */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Classroom Skills</label>
        <p className="text-xs text-gray-500 mb-2">Which rooms this staff is qualified to cover.</p>
        <div className="flex flex-wrap gap-2">
          {classrooms.map(c => (
            <button key={c.id} type="button" onClick={() => toggleSkill(c.name)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                skills.has(c.name)
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Scheduling Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
          placeholder='e.g. "must take 1pm-2pm lunch", "prefers mornings", "drives carpool — needs 6:30pm out"'
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
      </div>

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <button onClick={save} disabled={saving}
          className="bg-orange-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}

function PerformanceTab({ staffId, role }) {
  const [kpis, setKpis] = useState([]);
  const [jobDesc, setJobDesc] = useState(null);
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, jobRes, talentRes] = await Promise.all([
        api.get(`/kpis/staff/${staffId}`),
        api.get(`/job-descriptions/${role}`).catch(() => ({ data: null })),
        api.get(`/staff/${staffId}/talent`).catch(() => ({ data: null })),
      ]);
      setKpis(kpiRes.data);
      setJobDesc(jobRes.data);
      setTalent(talentRes.data);
    } catch { toast.error('Failed to load performance'); }
    finally { setLoading(false); }
  }, [staffId, role]);

  useEffect(() => { load(); }, [load]);

  const recalc = async () => {
    setRecalculating(true);
    try {
      await api.post('/kpis/recalculate');
      toast.success('Recalculated');
      await load();
    } catch { toast.error('Failed to recalculate'); }
    finally { setRecalculating(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;

  // Group KPIs by area for display
  const groups = kpis.reduce((acc, k) => {
    const area = k.kpi_area || 'general';
    if (!acc[area]) acc[area] = [];
    acc[area].push(k);
    return acc;
  }, {});

  // Talent & DISC section — three states:
  //   1. Not completed yet → amber prompt card.
  //   2. Completed AND has DISC scores → DISC card + Reset button.
  //   3. Completed before migration 042 (no DISC data) → "no DISC yet,
  //      ask them to retake" card with Reset button.
  const discScores = talent?.talent_profile_scores;
  const completedAt = talent?.survey_completed_at;
  const hasDiscData = discScores && (
    Math.abs(discScores.D || 0) + Math.abs(discScores.I || 0)
    + Math.abs(discScores.S || 0) + Math.abs(discScores.C || 0) > 0
  );

  const resetAssessment = async () => {
    if (!window.confirm(
      "Reset this staff member's assessment? They'll be prompted to retake the next time they sign in. Their current Talent Profile and DISC results will be cleared."
    )) return;
    try {
      await api.post(`/staff/${staffId}/reset-assessment`);
      toast.success('Assessment cleared — staff will retake on next sign-in.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset');
    }
  };

  return (
    <div className="space-y-5">
      {completedAt ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-start justify-between mb-3 gap-3">
            <h3 className="text-base font-semibold text-gray-900">
              {hasDiscData ? 'DISC profile' : 'Talent & Culture Assessment'}
            </h3>
            <button onClick={resetAssessment}
              className="text-xs font-medium text-orange-600 hover:text-orange-700 border border-orange-200 hover:bg-orange-50 px-2.5 py-1 rounded-lg flex-shrink-0">
              Reset &amp; retake
            </button>
          </div>
          {hasDiscData ? (
            <DiscProfile scores={discScores} />
          ) : (
            <div className="text-sm text-gray-500">
              Completed {new Date(completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })},
              but before DISC questions were added.
              Click <strong>Reset &amp; retake</strong> to capture their DISC profile next time they sign in.
            </div>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
          This staff member hasn't completed the Talent &amp; Culture Assessment yet. They'll see the prompt on their dashboard the next time they sign in.
        </div>
      )}

      {/* Job description / responsibilities */}
      {jobDesc && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="mb-3">
            <h3 className="text-base font-semibold text-gray-900">{jobDesc.title || role}</h3>
            {jobDesc.summary && <p className="text-sm text-gray-600 mt-1">{jobDesc.summary}</p>}
          </div>
          {jobDesc.responsibilities && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Key Responsibilities</p>
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{jobDesc.responsibilities}</pre>
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Live KPIs</h3>
            <p className="text-xs text-gray-500">Auto-calculated from portal data + bbc-booking sync. Refreshed daily.</p>
          </div>
          <button onClick={recalc} disabled={recalculating}
            className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-50">
            {recalculating ? 'Recalculating…' : 'Recalculate now'}
          </button>
        </div>

        {kpis.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No KPIs assigned to this role yet.</p>
        ) : (
          <div className="space-y-5">
            {Object.entries(groups).map(([area, items]) => (
              <div key={area}>
                <p className="text-xs font-semibold uppercase text-gray-500 mb-2">{area}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {items.map(k => <KpiCard key={k.kpi_id} kpi={k} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ kpi }) {
  const value = kpi.value;
  const direction = kpi.target_direction || 'higher';
  const green = kpi.green_threshold != null ? Number(kpi.green_threshold) : null;
  const amber = kpi.amber_threshold != null ? Number(kpi.amber_threshold) : null;

  let tone = 'gray';
  if (value !== null && value !== undefined && green != null) {
    if (direction === 'higher') {
      tone = value >= green ? 'green' : (amber != null && value >= amber ? 'amber' : 'red');
    } else {
      tone = value <= green ? 'green' : (amber != null && value <= amber ? 'amber' : 'red');
    }
  }

  const toneClass = {
    green: 'border-green-200 bg-green-50/50',
    amber: 'border-amber-200 bg-amber-50/50',
    red: 'border-red-200 bg-red-50/50',
    gray: 'border-gray-200 bg-white',
  }[tone];
  const valueClass = {
    green: 'text-green-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    gray: 'text-gray-700',
  }[tone];

  const formatValue = () => {
    if (value === null || value === undefined) return '—';
    const v = Number(value);
    if (kpi.unit === 'percent') return `${v.toFixed(0)}%`;
    if (kpi.unit === 'dollars') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (kpi.unit === 'hours') return `${v.toFixed(1)}h`;
    if (kpi.unit === 'points' || kpi.unit === 'count') return v.toFixed(v % 1 === 0 ? 0 : 1);
    if (kpi.unit === 'ratio') return v.toFixed(1);
    return String(v);
  };

  const isStale = !kpi.calculated_at ||
    (new Date() - new Date(kpi.calculated_at)) > 7 * 24 * 60 * 60 * 1000;
  const noData = value === null || value === undefined;

  return (
    <div className={`rounded-xl border p-4 ${toneClass}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-gray-900">{kpi.kpi_name}</p>
          {kpi.description && <p className="text-xs text-gray-500 mt-0.5">{kpi.description}</p>}
        </div>
        {kpi.target_value && (
          <span className="text-xs bg-white border border-gray-200 px-2 py-0.5 rounded-full text-gray-600 whitespace-nowrap">
            target: {kpi.target_value}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className={`text-3xl font-bold ${valueClass}`}>{formatValue()}</span>
        {kpi.metadata?.explanation && (
          <span className="text-xs text-gray-500">{kpi.metadata.explanation}</span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
        {kpi.source_system && (
          <span className={`px-1.5 py-0.5 rounded ${
            kpi.source_system === 'booking' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {kpi.source_system}
          </span>
        )}
        {noData ? (
          <span className="text-amber-600">
            {kpi.data_source?.startsWith('booking_') ? 'Awaiting booking sync' : 'No data yet'}
          </span>
        ) : isStale ? (
          <span className="text-amber-600">Last calc {new Date(kpi.calculated_at).toLocaleDateString()}</span>
        ) : (
          <span>Updated {new Date(kpi.calculated_at).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  );
}

// ─── Goal scoring tab ─────────────────────────────────────
//
// Renders the per-staff goal matrix: categories → templates → scored
// periods. Each cell is an editable score (1-max_score). Saving updates
// the per-period score and recomputes the running average. Tier is shown
// at the top with the corresponding raise %.

function periodLabelsFor(template) {
  if (Array.isArray(template.period_labels) && template.period_labels.length > 0) {
    return template.period_labels;
  }
  const now = new Date();
  if (template.measurement_type === 'quarterly') {
    const quarters = [];
    for (let i = 3; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
      quarters.push(`${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`);
    }
    return quarters;
  }
  if (template.measurement_type === 'annual') {
    return [String(now.getFullYear() - 1), String(now.getFullYear())];
  }
  if (template.measurement_type === 'one_time') {
    return ['Current'];
  }
  // default: monthly, trailing 6 months including current
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

function GoalScoringTab({ staffId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const { user: me } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/goals/staff/${staffId}/matrix`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const isManagement = me && (me.role === 'director' || me.role === 'admin');

  const saveCell = async (templateId, periodLabel, value, notes) => {
    const key = `${templateId}|${periodLabel}`;
    setSavingKey(key);
    try {
      await api.post(`/goals/staff/${staffId}/score`, {
        goal_template_id: templateId,
        period_label: periodLabel,
        score: value,
        notes: notes || null,
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save score');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  if (!data.categories || data.categories.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-500">
          No goal templates set up for the <strong>{data.role}</strong> role yet.
        </p>
        <Link to="/goals/templates" className="text-sm text-orange-500 hover:underline mt-2 inline-block">
          Set up goal templates →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary header */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Avg score</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {data.avg_score != null ? Number(data.avg_score).toFixed(2) : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Across {data.total_scored} scored periods</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Compensation tier</p>
          {data.tier ? (
            <>
              <p className="text-lg font-semibold text-gray-900 mt-1">{data.tier.label}</p>
              <p className="text-xs text-orange-600 mt-1 font-medium">+{Number(data.tier.raise_percent).toFixed(1)}% raise eligible</p>
            </>
          ) : (
            <p className="text-sm text-gray-400 mt-2">{data.avg_score == null ? 'Not enough scored data yet' : 'No tier match for this avg'}</p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Tier ladder ({data.role})</p>
          <div className="mt-1 space-y-0.5">
            {(data.tiers || []).map(t => (
              <div key={t.id} className={`text-xs flex justify-between ${data.tier && data.tier.id === t.id ? 'font-semibold text-orange-700' : 'text-gray-500'}`}>
                <span>≥ {Number(t.min_avg_score).toFixed(1)}: {t.label}</span>
                <span>+{Number(t.raise_percent).toFixed(1)}%</span>
              </div>
            ))}
            {(!data.tiers || data.tiers.length === 0) && (
              <Link to="/goals/comp-tiers" className="text-xs text-orange-500 hover:underline">Set up tiers →</Link>
            )}
          </div>
        </div>
      </div>

      {/* Goal categories + matrix */}
      {data.categories.map(cat => (
        <div key={cat.name} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">{cat.name}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase">
                  <th className="text-left px-4 py-2 sticky left-0 bg-white">Goal</th>
                  {/* Period header row computed below per-template; use first template's labels as the column headers */}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {cat.templates.map(t => {
                  const periods = periodLabelsFor(t);
                  const scoresByPeriod = (t.scores || []).reduce((acc, s) => {
                    acc[s.period_label] = s;
                    return acc;
                  }, {});
                  return (
                    <tr key={t.id} className="align-top">
                      <td className="px-4 py-3 sticky left-0 bg-white max-w-xs">
                        <p className="text-sm font-medium text-gray-900">{t.title}</p>
                        {t.rubric && (
                          <details className="mt-1">
                            <summary className="text-[11px] text-orange-500 cursor-pointer hover:underline">View rubric</summary>
                            <pre className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap bg-gray-50 p-2 rounded">{t.rubric}</pre>
                          </details>
                        )}
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Max {Number(t.max_score).toFixed(1)} · {t.measurement_type}
                        </p>
                      </td>
                      <td className="px-4 py-3" colSpan={periods.length}>
                        <div className="flex gap-2 flex-wrap">
                          {periods.map(p => (
                            <ScoreCell
                              key={`${t.id}|${p}`}
                              periodLabel={p}
                              score={scoresByPeriod[p]}
                              maxScore={Number(t.max_score)}
                              disabled={!isManagement}
                              saving={savingKey === `${t.id}|${p}`}
                              onSave={(value, notes) => saveCell(t.id, p, value, notes)}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoreCell({ periodLabel, score, maxScore, disabled, saving, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(score?.score != null ? String(score.score) : '');
  const [notes, setNotes] = useState(score?.notes || '');

  useEffect(() => {
    setVal(score?.score != null ? String(score.score) : '');
    setNotes(score?.notes || '');
  }, [score?.score, score?.notes]);

  const submit = async (e) => {
    e?.preventDefault();
    const num = val === '' ? null : Number(val);
    if (num != null && (num < 0 || num > maxScore)) {
      toast.error(`Score must be 0–${maxScore}`);
      return;
    }
    await onSave(num, notes);
    setEditing(false);
  };

  if (editing && !disabled) {
    return (
      <form onSubmit={submit} className="bg-white border border-orange-300 rounded-lg p-2 min-w-[140px]">
        <p className="text-[10px] text-gray-500 mb-1">{periodLabel}</p>
        <input type="number" step="0.5" min="0" max={maxScore}
          value={val} onChange={(e) => setVal(e.target.value)}
          autoFocus
          className="w-full rounded border border-gray-200 px-2 py-1 text-sm" />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Note (optional)"
          rows={2}
          className="w-full mt-1 rounded border border-gray-200 px-2 py-1 text-[11px] resize-y" />
        <div className="flex gap-1 mt-1">
          <button type="submit" disabled={saving}
            className="flex-1 bg-orange-500 text-white rounded text-xs py-1 hover:bg-orange-600 disabled:opacity-50">
            {saving ? '…' : 'Save'}
          </button>
          <button type="button" onClick={() => setEditing(false)}
            className="px-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
      </form>
    );
  }

  const empty = score?.score == null;
  return (
    <button type="button" disabled={disabled} onClick={() => setEditing(true)}
      className={`min-w-[110px] rounded-lg border px-3 py-2 text-left transition ${
        empty
          ? 'border-dashed border-gray-200 hover:border-orange-300 hover:bg-orange-50/30'
          : 'border-amber-200 bg-amber-50/40 hover:bg-amber-50'
      } ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}>
      <p className="text-[10px] text-gray-500">{periodLabel}</p>
      <p className={`text-sm font-bold ${empty ? 'text-gray-300' : 'text-amber-700'}`}>
        {empty ? '—' : `${Number(score.score).toFixed(1)} / ${maxScore}`}
      </p>
      {score?.notes && (
        <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1" title={score.notes}>{score.notes}</p>
      )}
    </button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Contact + Availability tab ───────────────────────────
// Staff edit their contact info inline (saves immediately).
// Availability changes go through admin approval — staff submits a request
// that the director sees in /availability-requests; on approval the values
// get written to portal_users.

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const labelize = (k) => k[0].toUpperCase() + k.slice(1);

function ContactAvailabilityTab({ staffId }) {
  const [contact, setContact] = useState(null);
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [submittingAvail, setSubmittingAvail] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, p, r, meRes] = await Promise.all([
        api.get(`/staff/${staffId}/contact`),
        api.get(`/staff/${staffId}/scheduling-profile`),
        api.get(`/staff/${staffId}/availability-requests`),
        api.get('/auth/me'),
      ]);
      setContact(c.data);
      setProfile(p.data);
      setRequests(r.data || []);
      setMe(meRes.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !contact || !profile) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const isSelf = me && Number(me.id) === Number(staffId);
  const isDirector = me && me.role === 'director';

  const saveContact = async (patch) => {
    setSavingContact(true);
    try {
      const { data } = await api.patch(`/staff/${staffId}/contact`, patch);
      setContact(data);
      toast.success('Contact info saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <div className="space-y-6">
      <ContactCard contact={contact} onSave={saveContact} disabled={savingContact} />

      <AvailabilityCard
        profile={profile}
        requests={requests}
        isSelf={isSelf}
        isDirector={isDirector}
        submitting={submittingAvail}
        onReload={load}
        onSubmitting={setSubmittingAvail}
        staffId={staffId}
      />
    </div>
  );
}

function ContactCard({ contact, onSave, disabled }) {
  const [form, setForm] = useState({
    phone: contact.phone || '',
    address: contact.address || '',
    birth_date: contact.birth_date?.slice(0, 10) || '',
    emergency_contact_name: contact.emergency_contact_name || '',
    emergency_contact_phone: contact.emergency_contact_phone || '',
    emergency_contact_relationship: contact.emergency_contact_relationship || '',
  });
  // birth_date deliberately absent — it is no longer editable here and the
  // contact endpoint no longer accepts it. Leaving it in this list would
  // mark the form dirty over a field that can never save.
  const dirty = ['phone','address','emergency_contact_name','emergency_contact_phone','emergency_contact_relationship']
    .some(k => (form[k] || '') !== ((contact[k]?.slice ? contact[k].slice(0, 10) : contact[k]) || ''));

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <IdentificationIcon className="w-4 h-4 text-gray-500" /> Contact information
      </h3>
      <p className="text-xs text-gray-500 mb-4">Saves immediately. Email is set during onboarding and managed by the director.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Email (read-only)">
          <input value={contact.email || ''} readOnly
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500" />
        </Field>
        <Field label="Phone">
          <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </Field>
        <Field label="Home address" className="md:col-span-2">
          <textarea value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}
            rows={2}
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm resize-y" />
        </Field>
        {/* ─── DATE OF BIRTH IS READ-ONLY HERE. 2026-09-15. ───────────
            It used to be an editable input on this form, saved by anyone
            the contact endpoint allowed — including the staff member
            themselves — with no validation and no audit row. DOB feeds the
            under-21 rostering rule.

            It now has its own director-only, validated, audited route
            (PATCH /api/staff/:staffId/birth-date). Shown here because it
            belongs on the profile; edited only by a director, and only
            with a reason once a verified State-Issued ID is on file. */}
        <Field label="Date of birth (director-only)">
          <input value={form.birth_date ? formatDateOnlyNumeric(form.birth_date) : '—'} readOnly
            title="Changed only by a director, with a reason recorded."
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500" />
        </Field>
        <Field label="Hire date (read-only)">
          <input value={contact.hire_date ? formatDateOnlyNumeric(contact.hire_date) : '—'} readOnly
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500" />
        </Field>
        <Field label="Emergency contact name">
          <input value={form.emergency_contact_name} onChange={(e) => setForm({...form, emergency_contact_name: e.target.value})}
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </Field>
        <Field label="Emergency contact phone">
          <input value={form.emergency_contact_phone} onChange={(e) => setForm({...form, emergency_contact_phone: e.target.value})}
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </Field>
        <Field label="Relationship">
          <input value={form.emergency_contact_relationship} onChange={(e) => setForm({...form, emergency_contact_relationship: e.target.value})}
            placeholder="Spouse, parent, sibling…"
            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </Field>
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={() => onSave(form)} disabled={!dirty || disabled}
          className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1">
          <CheckCircleIcon className="w-4 h-4" /> {disabled ? 'Saving…' : 'Save contact info'}
        </button>
      </div>
    </div>
  );
}

function AvailabilityCard({ profile, requests, isSelf, isDirector, onReload, onSubmitting, submitting, staffId }) {
  const [form, setForm] = useState(() => {
    const pat = profile.availability_pattern || {};
    return {
      type: profile.availability_type || 'fixed',
      pattern: WEEKDAYS.reduce((acc, d) => {
        const v = pat[d];
        if (!v || v === 'unavailable') acc[d] = { mode: 'unavailable' };
        else if (typeof v === 'object') acc[d] = { mode: 'available', start: v.start || '07:00', end: v.end || '17:00' };
        else acc[d] = { mode: 'unavailable' };
        return acc;
      }, {}),
      effective_date: '',
      reason: '',
    };
  });

  const pendingRequest = requests.find(r => r.status === 'pending');

  const buildPatchPayload = () => {
    const pattern = {};
    for (const d of WEEKDAYS) {
      const cell = form.pattern[d];
      if (cell.mode === 'unavailable') pattern[d] = 'unavailable';
      else pattern[d] = { start: cell.start, end: cell.end };
    }
    return {
      type: form.type,
      pattern: form.type === 'prn' ? null : pattern,
    };
  };

  const submitRequest = async () => {
    onSubmitting(true);
    try {
      const payload = buildPatchPayload();
      await api.post(`/staff/${staffId}/availability-request`, {
        proposed_availability_type: payload.type,
        proposed_availability_pattern: payload.pattern,
        effective_date: form.effective_date || null,
        reason: form.reason || null,
      });
      toast.success('Availability change submitted for director approval');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Submit failed');
    } finally {
      onSubmitting(false);
    }
  };

  const directorSaveDirectly = async () => {
    onSubmitting(true);
    try {
      const payload = buildPatchPayload();
      await api.patch(`/staff/${staffId}/scheduling-profile`, {
        availability_type: payload.type,
        availability_pattern: payload.pattern || {},
      });
      toast.success('Availability updated');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      onSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <CalendarDaysIcon className="w-4 h-4 text-gray-500" /> Availability
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        {isDirector
          ? 'You can edit availability directly. Staff edits go through approval first.'
          : 'Changes here will be sent to the director for approval before taking effect.'}
      </p>

      {pendingRequest && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Pending availability change</p>
            <p>Submitted {new Date(pendingRequest.requested_at).toLocaleString()} — waiting on director review.</p>
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-600 mb-2">Availability type</label>
        <div className="flex gap-2">
          <button onClick={() => setForm(f => ({...f, type: 'fixed'}))}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${form.type === 'fixed' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Fixed schedule (same days each week)
          </button>
          <button onClick={() => setForm(f => ({...f, type: 'prn'}))}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${form.type === 'prn' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            PRN / float (days vary)
          </button>
        </div>
        {form.type === 'prn' && (
          <p className="text-xs text-gray-500 mt-2">
            PRN staff don't get auto-drafted by the AI scheduler. The director will slot you in
            week-by-week as needed.
          </p>
        )}
      </div>

      {form.type === 'fixed' && (
        <div className="space-y-2 mb-4">
          {WEEKDAYS.map(d => (
            <div key={d} className="flex items-center gap-3">
              <span className="w-24 text-sm text-gray-700">{labelize(d)}</span>
              <select value={form.pattern[d].mode}
                onChange={(e) => setForm(f => ({
                  ...f,
                  pattern: { ...f.pattern, [d]: { ...f.pattern[d], mode: e.target.value }},
                }))}
                className="rounded-lg border border-gray-200 px-2 py-1 text-sm bg-white">
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </select>
              {form.pattern[d].mode === 'available' && (
                <>
                  <input type="time" value={form.pattern[d].start || '07:00'}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      pattern: { ...f.pattern, [d]: { ...f.pattern[d], start: e.target.value }},
                    }))}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm" />
                  <span className="text-gray-400">to</span>
                  <input type="time" value={form.pattern[d].end || '17:00'}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      pattern: { ...f.pattern, [d]: { ...f.pattern[d], end: e.target.value }},
                    }))}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm" />
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {!isDirector && isSelf && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <Field label="Effective starting (optional)">
            <input type="date" min={DATE_FLOOR} value={form.effective_date}
              onChange={(e) => setForm({...form, effective_date: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Reason for the change (optional)">
            <input value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})}
              placeholder="School schedule changed, second job, etc."
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
        </div>
      )}

      <div className="flex justify-end">
        {isDirector ? (
          <button onClick={directorSaveDirectly} disabled={submitting}
            className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" /> {submitting ? 'Saving…' : 'Save availability'}
          </button>
        ) : isSelf ? (
          <button onClick={submitRequest} disabled={submitting || !!pendingRequest}
            className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1">
            <PencilSquareIcon className="w-4 h-4" />
            {pendingRequest ? 'Pending review' : (submitting ? 'Submitting…' : 'Submit for approval')}
          </button>
        ) : null}
      </div>

      {requests.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-700 mb-2">Recent requests</p>
          <div className="space-y-1.5">
            {requests.slice(0, 5).map(r => (
              <div key={r.id} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">
                  {new Date(r.requested_at).toLocaleDateString()} —
                  proposed <strong>{r.proposed_availability_type}</strong>
                  {r.reason && <span className="text-gray-400"> · "{r.reason}"</span>}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  r.status === 'approved' ? 'bg-green-100 text-green-700' :
                  r.status === 'denied' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 6: Profile overview tab — editable position, pay history,
// documents store, employment-state actions (terminate / pause / unpause).
// ─────────────────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'director', label: 'Director' },
  { value: 'admin',    label: 'Office Admin' },
  { value: 'teacher',  label: 'Teacher (Full-Time)' },
  { value: 'float',    label: 'Float (Part-Time)' },
  { value: 'cook',     label: 'Cook' },
];
const EMPLOYMENT_OPTIONS = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'prn',       label: 'PRN / On-call' },
];

function ProfileOverviewTab({ staff, reload }) {
  const { isManagement, isDirector } = useAuth();
  return (
    <div className="space-y-6">
      {/* Recruiting-side data (contact, availability, talent profile,
          source, resume) — pulled from the candidates row linked to
          this portal_user. Director's ask 2026-06-11: "the recruitment
          profile is a lot more inclusive and complete." */}
      <RecruitingProfileCards staff={staff} />

      {/* Points Wallet — replaces the old attendance points model with a
          rewards currency system. Director-managed via Adjust Points
          button. Phase 2 (2026-06-12). */}
      <WalletBalanceCard userId={staff.id} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EditablePositionCard staff={staff} canEdit={isManagement} onSaved={reload} />
        <EmploymentActionsCard staff={staff} canEdit={isManagement} onChanged={reload} />
        <DocumentsCard staffId={staff.id} canEditMetadata={isManagement} />
        <PayHistoryCard staffId={staff.id} visible={isManagement} />
      </div>
    </div>
  );
}

// ─── "Nudge" REMOVED 2026-09-14 — Ramal's decision. ─────────────────
//
// The button sent a "you're X of Y done" progress email via
// POST /api/staff/:userId/nudge. Notices are held until the verification
// pass is complete, and a one-click send that ignores that hold is the
// same hazard as the roster-wide wave.
//
// THE ENDPOINT IS GATED SERVER-SIDE TOO (server.js), which is the durable
// half — a removed button is a UI decision and comes back the moment
// somebody rebuilds the page; the route gate does not.
//
// To restore: re-add the button AND confirm the hold is lifted. Do not
// restore the button alone.

// ── Set password (director-only) ────────────────────────────────────
//
// Bypasses the magic-link email flow when Resend / spam filters block
// the reset email and director needs to log in as a test user or
// unblock a staff member. Prompts for a new password inline and posts
// it to /staff/:id/set-password (director-only server side too).
function SetPasswordButton({ staffId, staffName }) {
  const { isDirector } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!isDirector) return null;
  const click = async () => {
    const pw = window.prompt(
      `Set a new password for ${staffName}.\n\n` +
      `Give them the password so they can log in and change it themselves.\n\n` +
      `Minimum 8 characters:`
    );
    if (!pw) return;
    if (String(pw).length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/staff/${staffId}/set-password`, { new_password: pw });
      toast.success(`Password set. Give it to ${staffName} to log in.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set password');
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
      className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50">
      <IdentificationIcon className="w-4 h-4" /> {busy ? 'Setting…' : 'Set Password'}
    </button>
  );
}

// ── Mark offer signed (director only) ───────────────────────────────
//
// For the case where staff's digital signature step failed silently
// (Danielle 2026-06-12) — they're stuck on the /my landing page
// stuck at "Step 1: sign your offer letter" even though they meant
// to. Director attests on their behalf; audit trail marks it as
// "(director-attested)" in signature_text so it's clear.
//
// Hidden once both timestamps are already set (nothing to force).
function MarkOfferSignedButton({ staff, onDone }) {
  const { isDirector } = useAuth();
  const [busy, setBusy] = useState(false);
  const control = offerAttestation(staff, isDirector);
  if (!control) return null;
  const click = async () => {
    if (!window.confirm(control.confirm)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${staff.id}/mark-offer-signed`);
      toast.success(control.success(data));
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
      className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50">
      <CheckIcon className="w-4 h-4" /> {busy ? 'Marking…' : 'Mark Offer Signed'}
    </button>
  );
}

// ── Void staff record (director only) ───────────────────────────────
//
// For duplicate portal accounts created by mistake (e.g. Cindy McGee
// had two records because the first email was wrong). End Employment
// leaves them Terminated but on file; Void wipes them entirely +
// deletes any linked candidate row. Requires typing the full name
// to confirm because this is truly destructive.
function VoidStaffButton({ staffId, staffName }) {
  const { isDirector } = useAuth();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  if (!isDirector) return null;
  const click = async () => {
    const typed = window.prompt(
      `PERMANENTLY DELETE ${staffName}'s portal account + any linked recruitment record?\n\n` +
      `This is not a soft delete. Use End Employment instead if you want the record kept for audit.\n\n` +
      `Type the full name to confirm:`
    );
    if (!typed || typed.trim().toLowerCase() !== staffName.toLowerCase()) {
      if (typed !== null) toast.error('Name did not match — void cancelled.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/staff/${staffId}/void`);
      toast.success(data.message || 'Record voided');
      navigate('/staff');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to void record');
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
      title="Director-only: permanently delete this staff account + any linked candidate record."
      className="bg-white border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50">
      <NoSymbolIcon className="w-4 h-4" /> {busy ? 'Voiding…' : 'Delete Record'}
    </button>
  );
}

// ── Reset onboarding (director only) ────────────────────────────────
//
// One-click "wipe this person's onboarding checklist back to pending."
// Director use case: a staff member whose items were missed by the
// batch reset migration, or whose status needs a fresh start (e.g.
// re-hire after termination). Confirms with full name typed to dodge
// destructive misclicks. Audit log captures every wiped row.
// ─── "Reset Onboarding" REMOVED 2026-09-14. Ramal's decision. ───────
//
// It DELETEd a person's onboarding_progress rows. Until migration 084 that
// orphaned every document hanging off them: the S3 object survived,
// referenced by nothing. That is the mechanism behind the 157 orphans the
// report found.
//
// USED EXACTLY ONCE, EVER — checked before removing it rather than after.
// Fourteen `reset` rows in file_audit_log, all written in twenty-six
// milliseconds on 2026-06-11 by Ramal against Laura Carmona: one click,
// one row per wiped requirement. It is nobody's workflow.
//
// THE ROUTE IS GATED SERVER-SIDE TOO (server.js, 409) — that is the
// durable half. A removed button comes back the moment somebody rebuilds
// the page; the route gate does not. Do not restore the button alone.
// ── Talent profile / contact / availability / source / resume ──
//
// Mirrors the cards on CandidateDetailPage's Overview tab. When a
// staff member doesn't have a candidate row (admin-created without
// going through recruitment), shows a single "no recruiting record"
// blurb so the section doesn't disappear without explanation.

const TALENT_PROFILES = {
  stabilizer: { name: 'Reliable Stabilizer',   blurb: 'Calm, consistent, follows through. Strong on structure and predictability.', color: 'bg-blue-500' },
  caregiver:  { name: 'Compassionate Caregiver', blurb: 'Emotionally attuned, listens first, prioritises harmony and connection.', color: 'bg-pink-500' },
  leader:     { name: 'Emerging Leader',       blurb: 'Decisive, direct, takes initiative and ownership.', color: 'bg-amber-500' },
  support:    { name: 'High Support Needed',   blurb: 'Defers to leadership and external structure.', color: 'bg-gray-500' },
  policy:     { name: 'Policy Risk',           blurb: 'Tendency to bend rules / treat process as optional.', color: 'bg-red-500' },
  reactivity: { name: 'Emotional Reactivity Risk', blurb: 'Pattern of withdrawal or frustration under stress.', color: 'bg-red-500' },
};

function formatRole(r) {
  return ({
    director: 'Director',
    admin: 'Office Admin',
    assistant_director: 'Asst. Director',
    teacher: 'Teacher',
    float: 'Float',
    cook: 'Cook',
  })[r] || r;
}

function RecruitingProfileCards({ staff }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/staff/${staff.id}/recruiting-profile`)
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [staff.id]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm text-center text-sm text-gray-400">
        Loading recruiting profile…
      </div>
    );
  }
  if (!data || !data.candidate_id) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm text-sm text-gray-500">
        No recruitment record linked. (This staff member was added
        directly without going through the recruitment pipeline.)
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ProfileCard title="Contact" icon={EnvelopeIcon}>
        <ProfileField label="Email" value={data.email || staff.email} />
        <ProfileField label="Phone" value={data.phone || staff.phone || '—'} />
        <ProfileField label="Role Applied" value={formatRole(data.role_applied)} />
        {data.referred_by && <ProfileField label="Referred By" value={data.referred_by} />}
      </ProfileCard>

      <ProfileCard title="Availability" icon={CalendarDaysIcon}>
        <ProfileField label="Days" value={data.availability_days || '—'} />
        <ProfileField label="Hours" value={data.availability_hours || '—'} />
        <ProfileField label="Earliest Start"
          value={data.earliest_start_date ? new Date(data.earliest_start_date).toLocaleDateString() : '—'} />
      </ProfileCard>

      <ProfileCard title="Source" icon={UserGroupIcon}>
        <ProfileField label="How They Came In"
          value={data.source === 'public_application' ? 'Online Application' : 'Admin Entry'} />
        <ProfileField label="Detail" value={data.source_detail || '—'} />
      </ProfileCard>

      <ProfileCard title="Resume" icon={DocumentTextIcon}>
        {data.resume_url ? (
          <a href={data.resume_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-orange-600 hover:underline text-sm font-medium">
            <DocumentTextIcon className="w-5 h-5" /> View Resume
          </a>
        ) : (
          <p className="text-sm text-gray-400">No resume uploaded</p>
        )}
      </ProfileCard>

      {data.survey_completed_at && data.talent_profile && (
        <div className="lg:col-span-2">
          <ProfileCard title="Talent Profile" icon={SparklesIcon}>
            <div className="grid md:grid-cols-[1fr_2fr] gap-6">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Primary profile</div>
                <div className="text-2xl font-bold text-gray-900">
                  {TALENT_PROFILES[data.talent_profile]?.name || data.talent_profile}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {TALENT_PROFILES[data.talent_profile]?.blurb}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {data.risk_policy && (
                    <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Policy Risk
                    </span>
                  )}
                  {data.risk_emotional_reactivity && (
                    <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Emotional Reactivity Risk
                    </span>
                  )}
                  {data.consistency_flag > 0 && (
                    <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      Consistency: {data.consistency_flag} contradiction{data.consistency_flag === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>
              {data.talent_profile_scores && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">All scores</div>
                  {Object.entries(data.talent_profile_scores)
                    .filter(([k]) => TALENT_PROFILES[k])
                    .sort(([, a], [, b]) => b - a)
                    .map(([profile, score]) => (
                      <div key={profile} className="flex items-center gap-3">
                        <div className="text-xs font-medium text-gray-700 w-44 truncate">
                          {TALENT_PROFILES[profile]?.name}
                        </div>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${TALENT_PROFILES[profile]?.color || 'bg-gray-400'} transition-all`}
                            style={{ width: `${Math.min(100, Math.max(0, Math.abs(score) * 5))}%` }}
                          />
                        </div>
                        <div className="text-xs font-mono text-gray-500 w-10 text-right">{score > 0 ? '+' : ''}{score}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </ProfileCard>
        </div>
      )}
    </div>
  );
}

function ProfileCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-5 h-5 text-orange-500" />}
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{value || '—'}</div>
    </div>
  );
}

// ── Edit Profile modal ──────────────────────────────────────────────
//
// Director's "Edit Profile" button on the staff page header. Edits
// first / last / email / phone on portal_users. Server validates
// duplicate emails (409 → clean toast).

// Same shape + same field set as the Recruitment "Edit candidate
// profile" modal so the staff page mirrors the recruitment page
// exactly. Fetches the linked candidate row on open and PATCHes the
// candidates endpoint. The recruitment endpoint mirrors the four
// shared fields (first/last/email/phone) back to portal_users so the
// staff header updates immediately.
function EditProfileModal({ staff, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [candidateId, setCandidateId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    api.get(`/staff/${staff.id}/recruiting-profile`)
      .then(({ data }) => {
        if (!data?.candidate_id) {
          setLoadErr('No candidate record linked yet — try refreshing in a moment, the backfill migration may still be running.');
          return;
        }
        setCandidateId(data.candidate_id);
        setForm({
          first_name: staff.first_name || '',
          last_name: staff.last_name || '',
          email: staff.email || data.email || '',
          phone: staff.phone || data.phone || '',
          role_applied: data.role_applied || staff.role || '',
          source_detail: data.source_detail || '',
          referred_by: data.referred_by || '',
          availability_days: data.availability_days || '',
          availability_hours: data.availability_hours || '',
          earliest_start_date: data.earliest_start_date ? data.earliest_start_date.slice(0, 10) : '',
          date_of_birth: data.date_of_birth ? data.date_of_birth.slice(0, 10) : '',
          state_of_birth: data.state_of_birth || '',
        });
      })
      .catch(() => setLoadErr('Could not load profile data.'));
  }, [staff.id, staff.first_name, staff.last_name, staff.email, staff.phone, staff.role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!candidateId || !form) return;
    setBusy(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      await api.patch(`/recruitment/candidates/${candidateId}`, cleaned);
      toast.success('Profile updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Edit profile</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>
        {loadErr && (
          <div className="p-6 text-sm text-gray-600">{loadErr}</div>
        )}
        {!loadErr && !form && (
          <div className="p-6 text-sm text-gray-400">Loading…</div>
        )}
        {form && (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input label="First name" value={form.first_name}
                onChange={(v) => setForm({ ...form, first_name: v })} required />
              <Input label="Last name" value={form.last_name}
                onChange={(v) => setForm({ ...form, last_name: v })} required />
            </div>
            <Input label="Email" type="email" value={form.email}
              onChange={(v) => setForm({ ...form, email: v })} required />
            <Input label="Phone" value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select value={form.role_applied}
                onChange={(e) => setForm({ ...form, role_applied: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white">
                <option value="director">Director</option>
                <option value="admin">Office Admin</option>
                <option value="teacher">Full-Time Teacher</option>
                <option value="float">Part-Time Float</option>
                <option value="cook">Cook</option>
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                Changes to this field also update the staff member's current portal role.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How they found us</label>
                <select value={form.source_detail}
                  onChange={(e) => setForm({ ...form, source_detail: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white">
                  <option value="">—</option>
                  <option value="indeed">Indeed</option>
                  <option value="referral">Referral</option>
                  <option value="walk_in">Walk-in</option>
                  <option value="website">Website</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <Input label="Referred by" value={form.referred_by}
                onChange={(v) => setForm({ ...form, referred_by: v })}
                placeholder="Name (if referral)" />
            </div>
            <Input label="Available days" value={form.availability_days}
              onChange={(v) => setForm({ ...form, availability_days: v })}
              placeholder="monday,tuesday,wednesday" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Available hours" value={form.availability_hours}
                onChange={(v) => setForm({ ...form, availability_hours: v })}
                placeholder="7am-3pm" />
              <Input label="Earliest start date" type="date" min={DATE_FLOOR}
                value={form.earliest_start_date}
                onChange={(v) => setForm({ ...form, earliest_start_date: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Date of birth" type="date" min={DOB_FLOOR} value={form.date_of_birth}
                onChange={(v) => setForm({ ...form, date_of_birth: v })} />
              <Input label="State of birth" value={form.state_of_birth}
                onChange={(v) => setForm({ ...form, state_of_birth: v.toUpperCase().slice(0, 2) })}
                placeholder="IN" />
            </div>
            <p className="text-[11px] text-gray-500 -mt-2">
              DOB + state of birth gate iLEAD consent, background check, and drug screen scheduling.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={busy}
                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white rounded-xl font-semibold">
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
    </div>
  );
}

// ── Editable position ────────────────────────────────────────────────────────

function EditablePositionCard({ staff, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    role: staff.role,
    employment_type: staff.employment_type || 'full_time',
    target_weekly_hours: staff.target_weekly_hours ?? '',
    hourly_pay: staff.hourly_pay ?? '',
  });
  const [payReason, setPayReason] = useState('annual_increase');
  const [payDescription, setPayDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const payChanged = String(staff.hourly_pay ?? '') !== String(form.hourly_pay ?? '');

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (payChanged) {
        body.pay_reason = payReason;
        body.pay_description = payDescription || null;
      }
      await api.patch(`/staff/${staff.id}/position`, body);
      toast.success('Position updated');
      setEditing(false);
      setPayDescription('');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <IdentificationIcon className="w-5 h-5 text-orange-500" /> Position &amp; Pay
        </h3>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)}
            className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
            <PencilSquareIcon className="w-4 h-4" /> Edit
          </button>
        )}
      </div>
      {!editing ? (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Detail label="Role" value={ROLE_OPTIONS.find(r => r.value === staff.role)?.label || staff.role} />
          <Detail label="Employment" value={EMPLOYMENT_OPTIONS.find(e => e.value === staff.employment_type)?.label || '—'} />
          <Detail label="Target hours / week" value={staff.target_weekly_hours ?? '—'} />
          <Detail label="Hourly pay" value={staff.hourly_pay ? `$${Number(staff.hourly_pay).toFixed(2)}` : '—'} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Employment</label>
              <select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                {EMPLOYMENT_OPTIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Target hours / week</label>
              <input type="number" min="0" max="60" value={form.target_weekly_hours}
                onChange={(e) => setForm({ ...form, target_weekly_hours: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hourly pay ($)</label>
              <input type="number" min="0" step="0.01" value={form.hourly_pay}
                onChange={(e) => setForm({ ...form, hourly_pay: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
            </div>
          </div>
          {payChanged && (
            <div className="border-t border-gray-100 pt-3 space-y-2 bg-amber-50/50 -mx-5 px-5 -mb-2 pb-3">
              <p className="text-xs font-medium text-amber-800">
                Pay changed — record the reason for the audit log.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                  <select value={payReason} onChange={(e) => setPayReason(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                    <option value="annual_increase">Annual increase</option>
                    <option value="promotion">Promotion</option>
                    <option value="role_change">Role change</option>
                    <option value="correction">Correction</option>
                    <option value="hire">Hire (initial pay)</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
                  <input value={payDescription} onChange={(e) => setPayDescription(e.target.value)}
                    placeholder="e.g. Promoted to lead teacher"
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white text-sm font-medium px-3 py-1.5 rounded-lg">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setForm({
              role: staff.role,
              employment_type: staff.employment_type || 'full_time',
              target_weekly_hours: staff.target_weekly_hours ?? '',
              hourly_pay: staff.hourly_pay ?? '',
            }); }}
              className="bg-white border border-gray-200 text-gray-600 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Employment actions ──────────────────────────────────────────────────────

function EmploymentActionsCard({ staff, canEdit, onChanged }) {
  const [modal, setModal] = useState(null); // 'terminate' | 'pause' | 'reinstate' | null
  const { user: me, isDirector } = useAuth();
  // #182: director-only, and never on your own record — the server refuses it too.
  const canReinstate = isDirector && me && Number(me.id) !== Number(staff.id);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <IdentificationIcon className="w-5 h-5 text-orange-500" /> Employment Status
      </h3>
      <div className="text-sm space-y-2">
        <Detail label="Status" value={
          <StatusPill status={staff.status} />
        } />
        {staff.status === 'paused' && staff.pause_reason && (
          <Detail label="Pause reason" value={staff.pause_reason} />
        )}
        {staff.status === 'paused' && staff.pause_until && (
          <Detail label="Pause until" value={formatDay(staff.pause_until)} />
        )}
        {staff.status === 'terminated' && (
          <>
            <Detail label="Type" value={staff.termination_type === 'resigned' ? 'Resigned' : 'Terminated'} />
            <Detail label="Last day" value={formatDay(staff.termination_date)} />
            {staff.termination_reason && <Detail label="Reason" value={staff.termination_reason} />}
            <Detail label="Rehirable" value={staff.rehirable === true ? 'Yes' : staff.rehirable === false ? 'No' : '—'} />
          </>
        )}
      </div>
      {canEdit && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
          {staff.status === 'paused' ? (
            <button onClick={async () => {
              if (!window.confirm(`Unpause ${staff.first_name}? They'll get portal access back immediately.`)) return;
              try {
                await api.post(`/staff/${staff.id}/unpause`);
                toast.success('Employment resumed');
                onChanged();
              } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
            }}
              className="bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
              <PlayCircleIcon className="w-4 h-4" /> Unpause
            </button>
          ) : ['active', 'onboarding', 'pending_hire', 'pending_clearance'].includes(staff.status) ? (
            <>
              <button onClick={() => setModal('pause')}
                className="bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 hover:bg-amber-100">
                <PauseCircleIcon className="w-4 h-4" /> Pause Employment
              </button>
              <button onClick={() => setModal('terminate')}
                className="bg-red-50 border border-red-200 text-red-700 text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 hover:bg-red-100">
                <NoSymbolIcon className="w-4 h-4" /> End Employment
              </button>
            </>
          ) : staff.status === 'terminated' && canReinstate ? (
            <button onClick={() => setModal('reinstate')}
              className="bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
              <PlayCircleIcon className="w-4 h-4" /> Reinstate
            </button>
          ) : staff.status === 'terminated' ? (
            <p className="text-xs text-gray-500">
              {isDirector ? 'Another director must reinstate your own record.' : 'Only a director can reinstate someone.'}
            </p>
          ) : (
            <p className="text-xs text-gray-500">No employment actions available for status &quot;{staff.status}&quot;</p>
          )}
        </div>
      )}

      {modal === 'reinstate' && (
        <ReinstateModal staff={staff} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />
      )}
      {modal === 'pause' && (
        <PauseModal staff={staff} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />
      )}
      {modal === 'terminate' && (
        <TerminateModal staff={staff} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active:            { label: 'Active',        cls: 'bg-green-100 text-green-700' },
    onboarding:        { label: 'Onboarding',    cls: 'bg-blue-100 text-blue-700' },
    pending_clearance: { label: 'Pre-clearance', cls: 'bg-blue-100 text-blue-700' },
    pending_hire:     { label: 'Pending hire',   cls: 'bg-blue-100 text-blue-700' },
    paused:            { label: 'Paused',        cls: 'bg-amber-100 text-amber-700' },
    terminated:        { label: 'Terminated',    cls: 'bg-red-100 text-red-700' },
  };
  const m = map[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

// #182. Two stages: the facts (reason, rehire date, first day worked), then a
// confirmation built from the SERVER's plan — the same code that does the
// write — listing every change before anything saves.
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ReinstateModal({ staff, onClose, onDone }) {
  const today = completionMax();
  const [form, setForm] = useState({ reason: '', hire_date: today, effective_start_date: '' });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = () => ({
    reason: form.reason,
    hire_date: form.hire_date || null,
    effective_start_date: form.effective_start_date,
  });

  const review = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${staff.id}/reinstate/preview`, body());
      setPlan(data);
    } catch (err) { toast.error(err.response?.data?.error || 'Could not prepare the reinstatement'); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/${staff.id}/reinstate`, body());
      toast.success(`${staff.first_name} reinstated`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Reinstatement failed'); }
    finally { setBusy(false); }
  };

  const d = (iso) => (iso ? formatDateOnlyNumeric(iso) : '—');

  return (
    <Modal title={`Reinstate — ${staff.first_name} ${staff.last_name}`} onClose={onClose}>
      {!plan ? (
        <form onSubmit={review} className="space-y-3">
          <p className="text-xs text-gray-600">
            Brings {staff.first_name} back as active staff. The termination record stays on file. You will see
            every change before anything is saved.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea required rows={2} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Rehired after interview on 10/27"
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rehire date <span className="text-red-500">*</span></label>
              <input type="date" required min={DATE_FLOOR} max={today} value={form.hire_date}
                onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <p className="text-[11px] text-gray-500 mt-1">The day the rehire was decided.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First day working <span className="text-red-500">*</span></label>
              <input type="date" required min={form.hire_date || DATE_FLOOR} value={form.effective_start_date}
                onChange={(e) => setForm({ ...form, effective_start_date: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <p className="text-[11px] text-gray-500 mt-1">Probation runs 60 days from this day.</p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={busy}
              className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-green-500/40 text-white font-semibold py-2 rounded-xl text-sm">
              {busy ? 'Checking…' : 'Review changes'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4 text-sm">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Will change</h4>
            <table className="w-full text-xs">
              <tbody>
                {plan.changes.map((c) => (
                  <tr key={c.field} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2 font-medium text-gray-700">{c.field}</td>
                    <td className="py-1.5 pr-2 text-gray-500">{c.field === 'Status' ? c.from : d(c.from)}</td>
                    <td className="py-1.5 pr-2">→</td>
                    <td className="py-1.5 pr-2 font-semibold text-gray-900">{c.field === 'Status' ? c.to : d(c.to)}</td>
                    <td className="py-1.5 text-gray-500">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Marked off until the first day working ({plan.gap_days_marked_off.length})
            </h4>
            {plan.gap_days_marked_off.length ? (
              <p className="text-xs text-gray-700">
                {plan.gap_days_marked_off.map((x) => `${DOW_SHORT[new Date(`${x}T12:00:00Z`).getUTCDay()]} ${d(x)}`).join(' · ')}
              </p>
            ) : <p className="text-xs text-gray-500">None — no scheduled days before {d(plan.effective_start_date)}.</p>}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Onboarding steps added ({plan.onboarding_steps.length})
            </h4>
            <p className="text-xs text-gray-600">
              {plan.onboarding_steps.map((s) => s.step_name).join(' · ') || 'None — every step already exists.'}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Policy acknowledgments</h4>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {plan.acknowledgments.map((a) => (
                <li key={a.title}>
                  {a.title}: {{
                    signed_current: 'already signed at the current version',
                    obligations: `owes ${a.versions} current version(s) — a deadline is set${
                      a.via === 'onboarding_step' ? ', and they sign through their onboarding step' : ''}`,
                    not_published: 'not published yet',
                  }[a.outcome] || a.outcome}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
              <ExclamationTriangleIcon className="w-4 h-4" /> Recurring schedule that becomes live again
            </h4>
            {plan.schedule_live_again.length ? (
              <>
                <ul className="text-xs text-amber-900 space-y-0.5">
                  {plan.schedule_live_again.map((s, i) => (
                    <li key={i}>
                      {DOW_SHORT[s.day_of_week]} {s.start_time}–{s.end_time}{s.classroom ? ` · ${s.classroom}` : ''}
                      {' '}(since {d(s.effective_from)}{s.effective_to ? `, until ${d(s.effective_to)}` : ', no end date'})
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-amber-800 mt-1">
                  Unchanged by this. From {d(plan.effective_start_date)} any day on it that is not worked is recorded as a
                  no-show — check it before sending the schedule out.
                </p>
              </>
            ) : <p className="text-xs text-amber-900">No recurring schedule on file.</p>}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Kept as it is</h4>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {plan.kept.map((k) => <li key={k.field}>{k.field}: {/^\d{4}-\d{2}-\d{2}$/.test(k.value || '') ? d(k.value) : (k.value || '—')}</li>)}
            </ul>
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button onClick={confirm} disabled={busy}
              className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-green-500/40 text-white font-semibold py-2 rounded-xl text-sm">
              {busy ? 'Reinstating…' : `Confirm — reinstate ${staff.first_name}`}
            </button>
            <button onClick={() => setPlan(null)} disabled={busy}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
              Back
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PauseModal({ staff, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [pauseUntil, setPauseUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/staff/${staff.id}/pause`, { reason, pause_until: pauseUntil || null });
      toast.success('Employment paused');
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Pause Employment — ${staff.first_name} ${staff.last_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-gray-600">Pauses employment without ending it. Portal access is revoked while paused; flip back to active to restore.</p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. School year — back in summer"
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Expected return (optional)</label>
          <input type="date" min={DATE_FLOOR} value={pauseUntil} onChange={(e) => setPauseUntil(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/40 text-white font-semibold py-2 rounded-xl text-sm">
            {saving ? 'Pausing…' : 'Pause employment'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TerminateModal({ staff, onClose, onDone }) {
  const [form, setForm] = useState({
    termination_type: 'terminated',
    reason: '',
    last_day: new Date().toISOString().slice(0, 10),
    rehirable: null,
  });
  const [letterFile, setLetterFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.last_day) return toast.error('Last day is required.');
    setSaving(true);
    try {
      // Upload letter first (if any) so we have a URL to attach to the termination.
      let letter_url = null;
      if (letterFile) {
        const fd = new FormData();
        fd.append('document', letterFile);
        fd.append('category', form.termination_type === 'resigned' ? 'resignation_letter' : 'term_letter');
        const { data } = await api.post(`/staff/${staff.id}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        letter_url = data.document_url;
      }
      await api.post(`/staff/${staff.id}/terminate`, {
        ...form,
        letter_url,
        rehirable: form.rehirable === '' ? null : form.rehirable === 'true',
      });
      toast.success('Employment ended');
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`End Employment — ${staff.first_name} ${staff.last_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-gray-600">
          Portal access is revoked immediately. The record stays for audit; everything you enter here is preserved.
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
          <select value={form.termination_type} onChange={(e) => setForm({ ...form, termination_type: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="terminated">Terminated (involuntary)</option>
            <option value="resigned">Resigned (voluntary)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Last day *</label>
          <input type="date" min={DATE_FLOOR} value={form.last_day} onChange={(e) => setForm({ ...form, last_day: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason / notes</label>
          <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Free-form context for the record"
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {form.termination_type === 'resigned' ? 'Resignation letter (optional)' : 'Termination letter (optional)'}
          </label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setLetterFile(e.target.files?.[0] || null)}
            className="w-full text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Rehirable?</label>
          <select
            value={form.rehirable === null ? '' : String(form.rehirable)}
            onChange={(e) => setForm({ ...form, rehirable: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-red-500/40 text-white font-semibold py-2 rounded-xl text-sm">
            {saving ? 'Ending…' : 'End employment'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Pay history ──────────────────────────────────────────────────────────────

function PayHistoryCard({ staffId, visible }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(visible);
  useEffect(() => {
    if (!visible) return;
    api.get(`/staff/${staffId}/pay-history`)
      .then(r => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [staffId, visible]);

  if (!visible) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm lg:col-span-2">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <BanknotesIcon className="w-5 h-5 text-orange-500" /> Pay History
      </h3>
      {loading ? <p className="text-sm text-gray-500">Loading…</p>
        : rows.length === 0 ? <p className="text-sm text-gray-500">No pay changes recorded yet.</p>
        : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="pb-2 font-medium">When</th>
                <th className="pb-2 font-medium">From → To</th>
                <th className="pb-2 font-medium">Reason</th>
                <th className="pb-2 font-medium">Description</th>
                <th className="pb-2 font-medium">By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-50">
                  <td className="py-2 text-gray-700">{formatDay(r.effective_date)}</td>
                  <td className="py-2 text-gray-900">
                    {r.old_pay ? `$${Number(r.old_pay).toFixed(2)}` : '—'} → ${Number(r.new_pay).toFixed(2)}
                  </td>
                  <td className="py-2 text-gray-700">{(r.reason || '').replace(/_/g, ' ')}</td>
                  <td className="py-2 text-gray-700 text-xs">{r.description || '—'}</td>
                  <td className="py-2 text-gray-500 text-xs">{r.changed_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

// ── Documents ────────────────────────────────────────────────────────────────

const DOC_CATEGORIES = [
  { value: 'hire_letter',        label: 'Hire letter' },
  { value: 'term_letter',        label: 'Termination letter' },
  { value: 'resignation_letter', label: 'Resignation letter' },
  { value: 'pay_change',         label: 'Pay change' },
  { value: 'performance_doc',    label: 'Performance doc' },
  { value: 'training_cert',      label: 'Training certificate' },
  { value: 'compliance_doc',     label: 'Compliance doc' },
  { value: 'background_check',   label: 'Background check result' },
  { value: 'handbook_ack',       label: 'Handbook acknowledgement' },
  { value: 'other',              label: 'Other' },
];

function DocumentsCard({ staffId, canEditMetadata }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // ONE READER. Option 1, approved 2026-10-04. This card read
  // /staff/:id/documents — staff_documents alone — while template uploads land
  // in onboarding_documents, so four people showed ZERO documents while holding
  // 18–22. It now reads the unified builder the Employment File reads.
  //
  // Only rows carrying a document are shown: requirement STATUS lives on the
  // Employment File, and putting it here too would make it true in two places.
  // A row whose link was redacted for this viewer still has a document, so it
  // stays — the row stays, only the link goes. Filter on `has_document`, set
  // before redaction: `url || access_restricted` would also admit an EMPTY W-4
  // row for an admin, because restriction is flagged by title, not by file.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/${staffId}/file`);
      const items = (data?.categories || [])
        .flatMap((c) => c.items)
        .filter((i) => i.has_document)
        .sort((a, b) => {
          if (!a.uploaded_at) return 1;
          if (!b.uploaded_at) return -1;
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
        });
      setDocs(items);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm lg:col-span-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-orange-500" /> Documents (paper file)
        </h3>
        <button onClick={() => setShowUpload(true)}
          className="text-sm text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
          <DocumentArrowUpIcon className="w-4 h-4" /> Upload
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Once uploaded, documents can't be deleted — only their metadata edited.
        Staff can upload their own; admins can edit any.
      </p>
      {loading ? <p className="text-sm text-gray-500">Loading…</p>
        : docs.length === 0 ? <p className="text-sm text-gray-500">No documents on file yet.</p>
        : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="pb-2 font-medium">Category</th>
                <th className="pb-2 font-medium">File</th>
                <th className="pb-2 font-medium">Completed</th>
                <th className="pb-2 font-medium">Expires</th>
                <th className="pb-2 font-medium">Uploaded</th>
                {canEditMetadata && <th className="pb-2 font-medium text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {docs.map(d => {
                const completed = d.completion_date || d.external_completion_date;
                return (
                <tr key={d.id} className="border-b border-gray-50">
                  <td className="py-2 text-gray-900">
                    {d.category
                      ? (DOC_CATEGORIES.find(c => c.value === d.category)?.label || d.category)
                      : d.title}
                  </td>
                  <td className="py-2">
                    {/* item.url — PRESIGNED by the builder behind canReadDocument
                        and canReadCategory. Never document_url: that is the raw
                        S3 path, which the bucket refuses and which bypasses both
                        access checks. */}
                    {d.url ? (
                      <a href={d.url} target="_blank" rel="noreferrer"
                        className="text-orange-600 hover:underline inline-flex items-center gap-1">
                        {d.original_filename || 'View'} <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <span className="text-gray-500" title={d.access_restricted_reason || ''}>
                        {d.original_filename || 'On file'} — restricted
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-gray-700">{completed ? formatDay(completed) : '—'}</td>
                  <td className="py-2 text-gray-700">{d.expiration_date ? formatDay(d.expiration_date) : '—'}</td>
                  <td className="py-2 text-gray-500 text-xs">
                    {d.uploaded_at ? formatDate(d.uploaded_at) : '—'}<br/>
                    by {d.uploaded_by_name || '—'}
                  </td>
                  {canEditMetadata && (
                    <td className="py-2 text-right">
                      {/* The PATCH endpoint addresses staff_documents only. A
                          button that cannot work must not appear. */}
                      {d.source_table === 'staff_documents' && (
                        <button onClick={() => setEditingId(d.id)} className="text-xs text-gray-600 hover:text-gray-900">
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

      {showUpload && (
        <DocumentUploadModal staffId={staffId} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />
      )}
      {editingId && (
        <DocumentEditModal doc={docs.find(d => d.id === editingId)} staffId={staffId}
          onClose={() => setEditingId(null)} onDone={() => { setEditingId(null); load(); }} />
      )}
    </div>
  );
}

function DocumentUploadModal({ staffId, onClose, onDone }) {
  const [form, setForm] = useState({ category: 'training_cert', notes: '', completion_date: '', expiration_date: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Pick a file first.');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('category', form.category);
      if (form.notes) fd.append('notes', form.notes);
      if (form.completion_date) fd.append('completion_date', form.completion_date);
      if (form.expiration_date) fd.append('expiration_date', form.expiration_date);
      await api.post(`/staff/${staffId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Upload document" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">File *</label>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Completion date</label>
            <input type="date" min={DATE_FLOOR} max={completionMax()} value={form.completion_date}
              onChange={(e) => setForm({ ...form, completion_date: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expires</label>
            <input type="date" min={DATE_FLOOR} max={expiryMax()} value={form.expiration_date}
              onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold py-2 rounded-xl text-sm">
            {saving ? 'Uploading…' : 'Upload'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DocumentEditModal({ doc, staffId, onClose, onDone }) {
  // NO NOTES FIELD. Ruled 2026-09-12: nothing renders from an internal note.
  // This modal seeded `notes` into an editable input — a live leak of the
  // engineering record — and PATCHed it back, so once the card moved to the
  // unified reader (which never returns notes) every Save would have sent ''
  // and updateDocument would have NULLed the note. Notes are neither shown nor
  // sent; the PATCH whitelist leaves an absent key untouched.
  const [form, setForm] = useState({
    category: doc.category,
    completion_date: doc.completion_date?.slice(0, 10) || '',
    expiration_date: doc.expiration_date?.slice(0, 10) || '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/staff/${staffId}/documents/${doc.source_id}`, form);
      toast.success('Document updated');
      onDone();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Edit document metadata" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-gray-600">The underlying file can't be edited — re-upload a corrected version if needed. Only metadata is editable.</p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Completion date</label>
            <input type="date" min={DATE_FLOOR} max={completionMax()} value={form.completion_date}
              onChange={(e) => setForm({ ...form, completion_date: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expires</label>
            <input type="date" min={DATE_FLOOR} max={expiryMax()} value={form.expiration_date}
              onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold py-2 rounded-xl text-sm">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Admin's "File" tab — reuses the shared <EmployeeFile> renderer with
// the staff-side aggregator endpoint. Same view the staff member sees
// at /my/file, so what admin sees here is exactly what they see.
function StaffFileTab({ staffId }) {
  // DIRECTOR ONLY for the verified-date correction control. `isManagement`
  // would be wrong here: it includes the office admins, who have their own
  // records in this system.
  const { isDirector } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/staff/${staffId}/file`)
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Could not load staff file.'))
      .finally(() => setLoading(false));
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-orange-500" />
      </div>
    );
  }
  if (!data) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Employment File</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Same view the staff member sees at My Employment File. {data.total_items} item{data.total_items === 1 ? '' : 's'}.
          </p>
        </div>
      </div>
      <EmployeeFile
        data={data}
        embedded
        canUpload
        uploadTargetId={Number(staffId)}
        adminManageUserId={Number(staffId)}
        // DIRECTOR ONLY — not admin. Rewriting a verified date is a director
        // act; the server enforces the same boundary with a 403.
        canCorrectVerifiedDates={isDirector}
        onUploaded={load}
      />
    </div>
  );
}
